<template>
    <Combobox as="div" v-model="selectedOption" @update:modelValue="query = ''" :multiple="multiple" :by="compareOptions" v-slot="{ open }">
        <ComboboxLabel v-if="label" class="block text-sm/6 font-medium text-gray-900">{{ label }}</ComboboxLabel>
        <div class="relative" :class="{ 'mt-2': label }">
            <div v-if="multiple && selectedOption.length" class="mb-2 flex flex-wrap gap-2">
                <span
                    v-for="option in selectedOption"
                    :key="option.id || option.value"
                    class="flex items-center rounded bg-indigo-100 px-2 py-1 text-sm text-indigo-700"
                >
                    <span class="font-medium">{{ option.text }}</span>
                    <span v-if="option.extra" class="ml-2 text-sm text-gray-600">{{ option.extra }}</span>
                    <button @click.stop="removeOption(option, true)" class="ml-1 cursor-pointer focus:outline-none">
                        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
                    </button>
                </span>
            </div>
            <!-- The chosen option sits on top of the input. It is bounded on the
                 right by the clear and chevron buttons and truncated inside that
                 box: unbounded, a long product name ran straight out of the field
                 and across whatever field came next. `pointer-events-none` on the
                 strip keeps the space beside the chip clickable as input.
                 The chip only has to beat its own field, so it stays at z-1: at
                 z-10 it tied with the inline option list below, and since no
                 ancestor here opens a stacking context the tie fell to DOM order
                 — a filled product row painted its chip straight over the
                 supplier dropdown opened above it. -->
            <div
                v-if="!multiple && selectedOption && selectedOption.value"
                class="pointer-events-none absolute inset-y-0 right-[4.5rem] left-1 z-[1] flex items-center"
            >
                <span
                    :key="selectedOption.id || selectedOption.value"
                    class="pointer-events-auto flex max-w-full min-w-0 items-center rounded bg-indigo-100 px-2 py-1 text-sm text-indigo-700"
                >
                    <span class="truncate font-medium">{{ selectedOption.text }}</span>
                    <span v-if="selectedOption.extra" class="ml-2 truncate text-sm text-gray-600">{{ selectedOption.extra }}</span>
                    <button @click.stop="removeOption(selectedOption)" class="ml-1 shrink-0 cursor-pointer focus:outline-none">
                        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
                    </button>
                </span>
            </div>
            <div class="relative" ref="fieldRef">
                <LoadingOverlay>
                    <ComboboxInput
                        :placeholder="!multiple && selectedOption && selectedOption.value ? '' : placeholderText"
                        class="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                        @change="queryValueChanged($event.target.value)"
                        @blur="updateQueryTextOnBlur"
                        :display-value="getDisplayText"
                        @click="handleInputClick(open)"
                        @focus="handleInputFocus"
                    />

                    <button
                        type="button"
                        v-if="!multiple && selectedOption"
                        class="absolute inset-y-0 right-8 flex cursor-pointer items-center rounded-r-md px-2 focus:outline-hidden"
                        @click.prevent="selectedOption = null"
                    >
                        <XMarkIcon class="size-5 text-gray-400" aria-hidden="true" />
                    </button>

                    <ComboboxButton ref="comboButton" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                        <ChevronUpDownIcon class="size-5 text-gray-400" aria-hidden="true" />
                    </ComboboxButton>
                </LoadingOverlay>
            </div>
            <!-- `floating` lifts the list out of the flow so a clipping ancestor
                 (a scrolling table, an overflow-hidden card) cannot cut it off. -->
            <Teleport to="body" :disabled="!floating">
                <div v-show="open" ref="floatRef" :class="floating ? 'fixed z-[420]' : ''" :style="floating ? floatStyle : null">
                    <ComboboxOptions
                        static
                        v-if="filteredOptions.length > 0"
                        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
                        :class="floating && floatPlacement === 'top' ? 'bottom-full mt-0 mb-1' : ''"
                    >
                        <ComboboxOption
                            v-for="option in filteredOptions"
                            :key="option.id ? option.id : option.value"
                            :value="option"
                            as="template"
                            v-slot="{ active, selected }"
                        >
                            <li
                                :class="[
                                    'relative cursor-default py-2 pr-9 pl-3 select-none',
                                    active ? 'activeComboOption bg-indigo-600 text-white outline-hidden' : 'text-gray-900',
                                ]"
                            >
                                <div class="flex flex-col">
                                    <div class="flex items-center">
                                        <img v-if="option.url" :src="option.url" alt="" class="mr-2 size-6 shrink-0 rounded-full" />
                                        <component v-else-if="option.icon" :is="option.icon" class="mr-2 size-4 shrink-0 rounded-full" />
                                        <span :class="['truncate', selected && 'font-semibold']">
                                            {{ option.text }}
                                            <span class="sr-only" v-if="option.online"> is {{ option.online ? 'online' : 'offline' }}</span>
                                        </span>
                                        <span
                                            v-if="option.online !== undefined"
                                            :class="[
                                                'ml-2 inline-block size-2 shrink-0 rounded-full',
                                                option.online ? 'bg-green-400' : 'bg-gray-200',
                                            ]"
                                            aria-hidden="true"
                                        />
                                        <!-- Advisory only. The option is still
                                             selectable; see `flaggedValues`. -->
                                        <span
                                            v-if="isFlagged(option)"
                                            :class="[
                                                'ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                                                active ? 'bg-indigo-500 text-indigo-50' : 'bg-amber-100 text-amber-700',
                                            ]"
                                        >
                                            {{ flagLabel }}
                                        </span>
                                    </div>
                                    <div v-if="option.extra" class="flex items-center">
                                        <span :class="['truncate text-gray-500', active ? 'text-indigo-200' : 'text-gray-500']">
                                            {{ option.extra }}
                                        </span>
                                    </div>
                                </div>

                                <span
                                    v-if="selected"
                                    :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']"
                                >
                                    <CheckIcon class="size-5" aria-hidden="true" />
                                </span>
                            </li>
                        </ComboboxOption>
                    </ComboboxOptions>
                </div>
            </Teleport>
        </div>
    </Combobox>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions } from '@headlessui/vue';
import apiClient from '@/utils/axios.js';
import { useDebounceFn } from '@vueuse/core';
import LoadingOverlay from '@/components/LoadingOverlay.vue';

// options = [{ id: 1, text: 'Leslie Alexander', value: '', icon: CheckIcon, url: '', extra: '' }, ...]
const props = defineProps({
    modelValue: {
        type: [String, Object, Array],
        default: null,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    dataSource: {
        type: String,
        default: '',
    },
    options: {
        type: [Array, String],
        default: () => [],
    },
    label: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: 'Search and Select an option',
    },
    autoClearSearch: {
        type: Boolean,
        default: true,
    },
    // Hold the first `dataSource` request until the field is actually used.
    // Pages that render one select per row would otherwise fire one request
    // per row on mount.
    deferInitialFetch: {
        type: Boolean,
        default: false,
    },
    // Teleport the option list to <body> and pin it to the field, for use
    // inside scrolling or overflow-hidden containers that would clip it.
    floating: {
        type: Boolean,
        default: false,
    },
    // Option values another field of the same form has already taken. They stay
    // in the list and stay selectable — a form of one select per row cannot
    // assume a repeat is a mistake, and a purchase invoice legitimately lists
    // one product on two lines, one per batch — but they are marked, so picking
    // one twice is a decision rather than an accident.
    flaggedValues: {
        type: Array,
        default: () => [],
    },
    flagLabel: {
        type: String,
        default: 'Already added',
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

const query = ref('');
const selectedOption = ref(props.multiple ? (Array.isArray(props.modelValue) ? props.modelValue : []) : (props.modelValue ?? ''));
const optionList = ref(props.options);

const comboButton = ref(null);
const fieldRef = ref(null);
const floatRef = ref(null);
const floatStyle = ref({ top: '0px', left: '0px', width: '0px' });
const floatPlacement = ref('bottom');

// max-h-60 (240px) plus the 4px offset the list renders with.
const FLOAT_LIST_HEIGHT = 244;

const updateFloatPosition = () => {
    if (!props.floating || !fieldRef.value) return;
    const rect = fieldRef.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    floatPlacement.value = spaceBelow < FLOAT_LIST_HEIGHT && rect.top > spaceBelow ? 'top' : 'bottom';
    floatStyle.value = {
        top: `${Math.round(floatPlacement.value === 'top' ? rect.top : rect.bottom)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
    };
};

// The list opens on the same tick, so measure again once it is laid out.
const scheduleFloatUpdate = () => {
    if (!props.floating) return;
    updateFloatPosition();
    nextTick(updateFloatPosition);
};

// v-show toggles display, so that is our "is the list on screen" signal.
const onViewportChange = () => {
    if (props.floating && floatRef.value && floatRef.value.style.display !== 'none') {
        updateFloatPosition();
    }
};

const handleInputClick = (open) => {
    ensureInitialOptions();
    scheduleFloatUpdate();
    if (!open && comboButton.value) {
        comboButton.value.el.click();
    }
};

const handleInputFocus = () => {
    ensureInitialOptions();
    scheduleFloatUpdate();
};

const placeholderText = computed(() => {
    if (props.placeholder) {
        return props.placeholder;
    }
    if (props.multiple) {
        return 'Search and Select Options ...';
    }
    if (selectedOption.value && selectedOption.value.value) {
        return '';
    }
    return 'Search and Select an option';
});

const searching = ref(false);
const initialFetchDone = ref(false);
const getOptionsFromDataSource = async () => {
    if (props.dataSource) {
        initialFetchDone.value = true;
        try {
            while (searching.value) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            searching.value = true;
            const response = await apiClient.post(props.dataSource, {
                search: query.value,
            });
            optionList.value = response.data.data;
        } catch (error) {
            console.error('Error fetching options:', error);
            optionList.value = [];
        } finally {
            searching.value = false;
        }
    } else {
        optionList.value = props.options;
    }
};

// Populates the unfiltered list the first time the field is opened. Cheap to
// call repeatedly — after the first fetch (or any search) it is a no-op.
const ensureInitialOptions = () => {
    if (!props.dataSource || initialFetchDone.value) return;
    getOptionsFromDataSource();
};

watch(
    () => props.dataSource,
    (newValue) => {
        if (newValue) {
            initialFetchDone.value = false;
            if (!props.deferInitialFetch) {
                getOptionsFromDataSource();
            }
        }
    },
);

// `optionList` is only seeded from props.options on mount, so a parent that
// loads its options asynchronously would render an empty dropdown forever.
// Guarded on !dataSource so the server-search path keeps owning its own list.
watch(
    () => props.options,
    (next) => {
        if (!props.dataSource) {
            optionList.value = next ?? [];
        }
    },
);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedOption.value = props.multiple ? (Array.isArray(newValue) ? newValue : []) : newValue;
        emit('change', [selectedOption.value, query.value]);
    },
);
// `watch`, not `watchEffect`. `emit` runs the parent's handler synchronously, so
// under `watchEffect` every reactive value that handler *read* became a
// dependency of this effect — and a handler that also writes one of them (the
// purchase screen caches the chosen variant that way) re-triggered the effect
// forever. Two selects bound to one model (the responsive card and table copies
// of a row) even re-triggered each other, so Vue's own "am I already running"
// guard could not stop it: the built bundle drops the recursion limit that keeps
// this to a warning in development, and the tab froze on the first product pick.
// A `watch` callback runs outside the tracking scope, so only `selectedOption`
// is ever a dependency.
watch(
    selectedOption,
    (value) => {
        emit('update:modelValue', value);
    },
    { immediate: true },
);

// Compared as strings: an id arrives as a number from a server option and as a
// string from a form field, and `1 !== '1'` would quietly match nothing.
const flaggedValues = computed(() => new Set((props.flaggedValues ?? []).map((value) => String(value))));

// Marks the option, never filters it — `filteredOptions` is deliberately blind
// to this set.
const isFlagged = (option) => flaggedValues.value.size > 0 && flaggedValues.value.has(String(option.value ?? option.id));

const filteredOptions = computed(() =>
    query.value === ''
        ? optionList.value
        : optionList.value.filter((option) => {
              if (option.extra) {
                  return (
                      option.text.toLowerCase().includes(query.value.toLowerCase()) || option.extra.toLowerCase().includes(query.value.toLowerCase())
                  );
              }
              return option.text.toLowerCase().includes(query.value.toLowerCase());
          }),
);

const compareOptions = (a, b) => {
    if (a && b && typeof a === 'object' && typeof b === 'object') {
        if ('id' in a && 'id' in b) {
            return a.id === b.id;
        }
        if ('value' in a && 'value' in b) {
            let first = a.value + '';
            let second = b.value + '';
            return first.toLowerCase() === second.toLowerCase();
        }
        return a === b;
    }
    return a === b;
};

const removeSelected = (value) => {
    selectedOption.value = selectedOption.value.filter((v) => !compareOptions(v, value));
    emit('update:modelValue', selectedOption.value);
};

const getDisplayText = () => {
    if (props.autoClearSearch) {
        return '';
    } else {
        if (!props.multiple) {
            if (selectedOption.value.value) {
                return '';
            }
        }
    }
};

const updateQueryTextOnBlur = () => {
    if (props.autoClearSearch) {
        query.value = '';
    } else {
        if (!props.multiple) {
            if (selectedOption.value.value) {
                query.value = '';
            }
        }
    }
};

const removeOption = (option, multiple = false) => {
    if (multiple) {
        selectedOption.value = selectedOption.value.filter((item) => !compareOptions(item, option));
        return;
    }
    selectedOption.value = null;
};

const debounceSearch = useDebounceFn(async () => {
    await getOptionsFromDataSource();
}, 600);

const queryValueChanged = (value) => {
    query.value = value;
};

watch(query, (newValue) => {
    if (props.dataSource) {
        debounceSearch();
    }
    scheduleFloatUpdate();
});

// A shorter list can fit below where a longer one could not, and vice versa.
watch(() => filteredOptions.value.length, scheduleFloatUpdate);

const getQueryText = () => {
    return query.value;
};

onMounted(async () => {
    if (props.dataSource) {
        if (!props.deferInitialFetch) {
            await getOptionsFromDataSource();
        }
    } else {
        optionList.value = props.options;
    }

    if (props.floating) {
        window.addEventListener('scroll', onViewportChange, true);
        window.addEventListener('resize', onViewportChange);
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', onViewportChange, true);
    window.removeEventListener('resize', onViewportChange);
});

defineExpose([getQueryText]);
</script>
